/**
 * @quotech/ui/kanban — per-category default-board enum + labels.
 *
 * Split out of the former monolith `kanban/api.ts` (operator 500-line
 * ceiling). Re-exported verbatim via `kanban/api/index.ts`, so every
 * `from '.../api'` / `@quotech/ui/kanban` import is unchanged.
 */

/**
 * FEAT-2026-05-25-BOARD-DEFAULTS-PER-CATEGORY. Workflow categories
 * an operator can designate a "primary board" for. Mirrors
 * `email_kanban.models.BoardDefaultCategory` on the backend
 * (pinned by `test_all_categories_match_api_constant` server-side
 * + `boardDefaultsTwin.test.ts` client-side).
 *
 * Categories are WORKFLOW-shaped, not HUB-shaped:
 *   - HOME — Kanban hub auto-resume + create-card destination
 *   - JOB — `/jobs` hub default landing
 *   - QUOTE — `/quotes` hub default (Quote Pipeline target)
 *   - FINANCE — `/bills` AND `/invoices` both honour this single
 *               default (one cash-flow workflow, two views)
 *   - EMAIL — right-click "Allocate to Kanban" submenu pre-select
 *   - BUILDER — `/quote-builder` hub default landing (operator added
 *               2026-05-25 as a follow-up: "do full hub coverage,
 *               but Qdrive is just pure ratrded so is calendar..
 *               fiancne covers bills and invoices..").
 *
 * QDrive + Calendar are deliberately absent — no "default board"
 * concept fits those hubs.
 */
export type BoardDefaultCategory =
  | 'HOME'
  | 'JOB'
  | 'QUOTE'
  | 'FINANCE'
  | 'EMAIL'
  | 'BUILDER'

/** Single source of truth for the category enum at runtime. Used
 *  by the FE for iteration (the menu submenu builder, hook bag
 *  hydration) and pinned to match the BE enum via the source-pin
 *  test in `boardDefaultsTwin.test.ts`. */
export const BOARD_DEFAULT_CATEGORIES: readonly BoardDefaultCategory[] = [
  'HOME', 'JOB', 'QUOTE', 'FINANCE', 'EMAIL', 'BUILDER',
] as const

/** Human-readable label per category — used by the context menu
 *  submenu, the toast text on set/clear, and any hub-level "you
 *  landed here because <CATEGORY> is your default" surface. Kept
 *  inside `@quotech/ui/kanban` so every consumer renders the same
 *  copy. */
export const BOARD_DEFAULT_CATEGORY_LABEL: Record<
  BoardDefaultCategory,
  string
> = {
  HOME: 'Home',
  JOB: 'Jobs',
  QUOTE: 'Quotes',
  FINANCE: 'Finance (Bills + Invoices)',
  EMAIL: 'Email',
  BUILDER: 'Builder',
}

/** The full per-category bag returned by `getBoardDefaults` — every
 *  category is present, unset slots are explicitly null. This shape
 *  guarantees consumers can render every category's "is set?" badge
 *  without a presence check. */
export type BoardDefaultsBag = Record<BoardDefaultCategory, string | null>
